import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

import { useShopping } from "../../../contexts/shopping";
import useMutation from "../../../hooks/useMutation";
import { UPDATE_SHOPPING_INVENTORY_ITEM_MUTATION } from "../../../constants/graphql/mutations";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import CardImage from "../../../components/card/CardImage";
import ItemId from "../Orders/ItemId";
import { ProjectProvider } from "../../../contexts/project";
import ItemIdAssetModal from "../Orders/ItemIdAssetModal";
import QuantityEditModal from "../../inventory/requests/QuantityEditModal";

export function getImage(asset) {
  return asset?.photo || asset?.images?.length
    ? asset?.images.find(v => v.default)?.url || asset?.images[0].url
    : null;
}

export function getInventoryFields(inventoryItem) {
  return {
    projectId: inventoryItem.projectId,
    inventoryItemId: inventoryItem.id,
    quantity: inventoryItem.quantity,
    deliveryDate: inventoryItem.deliveryDate,
    deliveryTime: inventoryItem.deliveryTime,
    building: inventoryItem.building || "",
    floor: inventoryItem.floor || "",
    room: inventoryItem.room || "",
    comment: inventoryItem.comment || "",
    location: inventoryItem.location || "",
    street: inventoryItem.street || "",
    city: inventoryItem.city || "",
    zip: inventoryItem.zip || "",
    poNumber: inventoryItem.poNumber || "",
    idsrNumber: inventoryItem.idsrNumber || "",
    eocNumber: inventoryItem.eocNumber || "",
  };
}

export default function CartItemCard({ inventoryItem, asset, disabled, onDelete, display = {} }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const { refetch } = useShopping();
  const [updateInventoryItem] = useMutation(UPDATE_SHOPPING_INVENTORY_ITEM_MUTATION);
  const isRequiredCompanion = inventoryItem.requiredCompanion;

  const photo = getImage(asset);

  const handleQuantityChange = newQuantity => {
    const val =
      typeof newQuantity === "object" ? parseInt(newQuantity.target.value, 10) : newQuantity;
    if (val < 1) return;

    const hasKnownAvailability = Number.isFinite(asset?.availableQuantity);
    const maxQuantity = hasKnownAvailability ? inventoryItem.quantity + asset.availableQuantity : null;
    if (maxQuantity !== null && val > maxQuantity) {
      return Promise.resolve({ quantity: `Only ${maxQuantity} total can be requested right now` });
    }

    return new Promise(resolve => {
      updateInventoryItem({
        variables: getInventoryFields({
          ...inventoryItem,
          assetId: asset.id,
          quantity: val,
        }),
        onSuccess: () => {
          refetch();
          resolve(null);
        },
        onFailure: errors => resolve(errors),
      });
    });
  };

  const handleCardClick = (e) => {
    // Don't open modal if clicking on interactive elements
    if (e.target.closest('button, input, .MuiIconButton-root, .quantity-selector')) {
      return;
    }
    setDetailsOpen(true);
  };

  return (
    <>
      <Box
        elevation={0}
        variant="outlined"
        onClick={handleCardClick}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          mb: 2,
          overflow: "hidden",
          transition: "box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease",
          borderRadius: 2,
          p: 2,
          cursor: "pointer",
          backgroundColor: "white",
          "&:hover": {
            backgroundColor: "action.hover",
            boxShadow: 2,
          },
        }}
      >
        <CardImage
          image={photo}
          sx={{
            width: { xs: "100%", sm: 120 },
            height: { xs: 200, sm: 120 },
            flexShrink: 0,
            mr: { xs: 0, sm: 2 },
            mb: { xs: 2, sm: 0 },
            borderRadius: 1,
            color: "text.disabled",
          }}
        >
          No Image
        </CardImage>

        <Box
          sx={{
            flexGrow: 1,
            mr: { xs: 0, sm: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            position: "relative",
          }}
        >
          <ItemId asset={asset} />
          {isRequiredCompanion && (
            <Typography variant="caption" color="text.secondary">
              Required companion
            </Typography>
          )}
          <Typography variant="body2">
            {asset.description}
          </Typography>
          {display.ii_model && asset.model && (
            <Typography variant="body2" color="text.secondary">
              {asset.model}
            </Typography>
          )}
          {display.ii_sku_number && asset.skuNumber && (
            <Typography variant="body2" color="text.secondary">
              {asset.skuNumber}
            </Typography>
          )}

          {/* Remove button and quantity controls in bottom right */}
          {!disabled && !isRequiredCompanion && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Remove button */}
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteOpen(true);
                }}
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "text.secondary",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  textDecoration: "underline",
                  "&:hover": {
                    color: "text.primary"
                  }
                }}
              >
                Remove
              </Box>

              {/* Minus/plus stay for quick changes; the qty opens the direct-entry modal. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "16px",
                  px: 0.5,
                  backgroundColor: "white",
                  height: 28,
                }}
                className="quantity-selector"
              >
                <IconButton
                  size="small"
                  onClick={() => handleQuantityChange(inventoryItem.quantity - 1)}
                  disabled={inventoryItem.quantity <= 1}
                  sx={{ 
                    width: 20, 
                    height: 20,
                    minWidth: 20,
                    padding: 0,
                    color: "text.secondary",
                    fontSize: "12px",
                    "&:hover": { backgroundColor: "transparent" },
                    "&.Mui-disabled": { color: "action.disabled" }
                  }}
                >
                  <Typography fontSize="16px">−</Typography>
                </IconButton>
                <Box
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setQuantityModalOpen(true);
                  }}
                  sx={{
                    minWidth: 20,
                    mx: 0.5,
                    px: 0.5,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "text.primary",
                    fontSize: "13px",
                    fontWeight: 400,
                    fontFamily: "inherit",
                    textAlign: "center",
                  }}
                >
                  {inventoryItem.quantity}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleQuantityChange(inventoryItem.quantity + 1)}
                  disabled={Number.isFinite(asset?.availableQuantity) && asset.availableQuantity <= 0}
                  sx={{ 
                    width: 20, 
                    height: 20,
                    minWidth: 20,
                    padding: 0,
                    color: "text.secondary",
                    fontSize: "12px",
                    "&:hover": { backgroundColor: "transparent" }
                  }}
                >
                  <Typography fontSize="16px">+</Typography>
                </IconButton>
              </Box>
            </Box>
          )}

          {disabled && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Quantity: {inventoryItem.quantity}
            </Typography>
          )}
        </Box>
      </Box>

      {detailsOpen && (
        <ProjectProvider id={asset.project.id} loadingComponent={<span />}>
          <ItemIdAssetModal id={asset.id} onClose={() => setDetailsOpen(false)} />
        </ProjectProvider>
      )}

      {deleteOpen && (
        <ConfirmDeleteModal
          title="Remove Item"
          applyLabel="Remove"
          descr="Are you sure you want to remove this item from your cart?"
          onClose={() => setDeleteOpen(false)}
          onDelete={onDelete}
        />
      )}

      {quantityModalOpen && (
        <QuantityEditModal
          onClose={() => setQuantityModalOpen(false)}
          value={inventoryItem.quantity}
          availableQuantity={asset.availableQuantity}
          minQuantity={1}
          onChange={handleQuantityChange}
        />
      )}
    </>
  );
}
